import { ProtocolError } from "./envelope.ts";

export const TRANSFER_CHUNK_HEADER_BYTES = 44;

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Binary WebSocket frame format for file transfer chunks:
 *  - 0..36 (36 bytes): ASCII UUID `transferId`
 *  - 36..44 (8 bytes): BigInt64BE byte `offset`
 *  - 44..end: raw chunk payload bytes
 */

export function encodeTransferChunk(
  transferId: string,
  offset: number,
  chunk: Uint8Array,
): Uint8Array {
  if (!UUID_REGEX.test(transferId)) {
    throw new ProtocolError("invalid transferId format for binary chunk");
  }
  if (!Number.isSafeInteger(offset) || offset < 0) {
    throw new ProtocolError("invalid chunk offset");
  }
  const result = new Uint8Array(TRANSFER_CHUNK_HEADER_BYTES + chunk.byteLength);
  for (let i = 0; i < 36; i++) {
    result[i] = transferId.charCodeAt(i);
  }
  const view = new DataView(result.buffer, result.byteOffset, result.byteLength);
  view.setBigInt64(36, BigInt(offset), false);
  result.set(chunk, TRANSFER_CHUNK_HEADER_BYTES);
  return result;
}

export function decodeTransferChunk(
  data: Uint8Array,
): { transferId: string; offset: number; data: Uint8Array } {
  if (data.byteLength < TRANSFER_CHUNK_HEADER_BYTES) {
    throw new ProtocolError("binary frame too short to contain transfer chunk header");
  }
  let transferId = "";
  for (let i = 0; i < 36; i++) {
    const code = data[i];
    if (code === undefined) {
      throw new ProtocolError("binary frame too short to contain transfer chunk header");
    }
    transferId += String.fromCharCode(code);
  }
  if (!UUID_REGEX.test(transferId)) {
    throw new ProtocolError("invalid transferId in chunk header");
  }
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const offsetBigInt = view.getBigInt64(36, false);
  if (offsetBigInt < 0n || offsetBigInt > BigInt(Number.MAX_SAFE_INTEGER)) {
    throw new ProtocolError("invalid chunk offset in header");
  }
  const offset = Number(offsetBigInt);
  const chunkData = data.subarray(TRANSFER_CHUNK_HEADER_BYTES);
  return { transferId, offset, data: chunkData };
}
